// OWNER: platform module (offline area). Persistence for the offline outbox (ADR 0004 §2).
//
// IndexedDB database "seventide-offline", object store "outbox" (keyPath "id"). Uses the IndexedDB API
// directly (no extra dependency). If IndexedDB is unavailable (some private-browsing modes, blocked storage)
// the outbox falls back to memory: offline changes then survive only until the tab is closed.
//
// Privacy trade-off (documented in ADR 0004): items hold exactly what the user typed for a write that has not
// reached the server yet. They are NOT encrypted at rest — a key would have to live on the same device, so it
// would add little protection against someone with access to the browser profile. Instead the outbox is kept
// minimal: an item is deleted as soon as the server accepts it, conflict items drop their content, everything
// is cleared on sign-out, and API responses are never cached (see public/sw.js).
import type { OutboxItem } from './types';

export interface OutboxStore {
  all(): Promise<OutboxItem[]>;
  get(id: string): Promise<OutboxItem | undefined>;
  put(item: OutboxItem): Promise<void>;
  delete(id: string): Promise<void>;
  clear(): Promise<void>;
}

export const OUTBOX_DB_NAME = 'seventide-offline';
const DB_VERSION = 1;
const STORE = 'outbox';

export function createMemoryStore(): OutboxStore {
  const map = new Map<string, OutboxItem>();
  const copy = <T>(v: T): T => (v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T));
  return {
    all: async () => [...map.values()].map(copy),
    get: async (id) => copy(map.get(id)),
    put: async (item) => {
      map.set(item.id, copy(item));
    },
    delete: async (id) => {
      map.delete(id);
    },
    clear: async () => {
      map.clear();
    },
  };
}

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

function openDb(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = factory.open(OUTBOX_DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('by_workspace', 'workspaceId', { unique: false });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // Another tab upgraded the schema: close so it isn't blocked; we reopen lazily.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    req.onerror = () => reject(req.error ?? new Error('Could not open IndexedDB'));
    req.onblocked = () => reject(new Error('IndexedDB is blocked by another tab'));
  });
}

export function createIndexedDbStore(factory: IDBFactory): OutboxStore {
  let dbPromise: Promise<IDBDatabase> | null = null;
  const db = () => {
    if (!dbPromise) {
      dbPromise = openDb(factory).then((d) => {
        d.onclose = () => {
          dbPromise = null;
        };
        return d;
      });
      dbPromise.catch(() => {
        dbPromise = null;
      });
    }
    return dbPromise;
  };
  const run = async <T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const d = await db();
    const tx = d.transaction(STORE, mode);
    // Listen for completion before awaiting the request, so the event can't be missed.
    const done = new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    });
    done.catch(() => {});
    const result = await promisify(fn(tx.objectStore(STORE)));
    if (mode === 'readwrite') await done; // durable before we report success
    return result;
  };
  return {
    all: () => run('readonly', (s) => s.getAll() as IDBRequest<OutboxItem[]>),
    get: (id) => run('readonly', (s) => s.get(id) as IDBRequest<OutboxItem | undefined>),
    put: async (item) => {
      await run('readwrite', (s) => s.put(item));
    },
    delete: async (id) => {
      await run('readwrite', (s) => s.delete(id));
    },
    clear: async () => {
      await run('readwrite', (s) => s.clear());
    },
  };
}

/** IndexedDB when available, else memory. Falls back to memory if the first IndexedDB call fails. */
export function createDefaultStore(): OutboxStore {
  const factory = typeof indexedDB !== 'undefined' ? indexedDB : undefined;
  if (!factory) return createMemoryStore();
  const idb = createIndexedDbStore(factory);
  const memory = createMemoryStore();
  let active: OutboxStore = idb;
  let probed = false;
  const call = async <T>(fn: (s: OutboxStore) => Promise<T>): Promise<T> => {
    if (probed) return fn(active);
    try {
      const r = await fn(active);
      probed = true;
      return r;
    } catch {
      probed = true;
      active = memory;
      return fn(active);
    }
  };
  return {
    all: () => call((s) => s.all()),
    get: (id) => call((s) => s.get(id)),
    put: (item) => call((s) => s.put(item)),
    delete: (id) => call((s) => s.delete(id)),
    clear: () => call((s) => s.clear()),
  };
}
